import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import EditAccountModal from "./EditAccountModal";
import { formatMoney } from "../lib/format";
import { useSettings } from "../SettingsContext";
import StatementsGrid from "./StatementsGrid";
import RequirementsList from "./RequirementsList";
import InvoicesPanel from "./InvoicesPanel";
import DocumentsSection from "./DocumentsSection";

const AccountDetail = ({ account, onUpdate, logoUrl, onLogoUpdate, onFilesChange, onOpenStatement, statementsRefreshKey, extractingStatementIds, onExtract, onStatementsViewModeChange }) => {
  const { settings } = useSettings();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [docsRefresh, setDocsRefresh] = useState(0);

  useEffect(() => {
    fetchFiles();
    if (account.logo_filename && !logoUrl) {
      onLogoUpdate(account.id);
    }
  }, [account.id, account.logo_filename, logoUrl, onLogoUpdate]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/accounts/${account.id}/files`);
      setFiles(response.data);
    } catch (error) {
      console.error("Failed to fetch files:", error);
    }
  };

  const handleFileUpload = async (fileList) => {
    if (fileList.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(fileList).forEach((file) => formData.append("files", file));
    try {
      await axios.post(`${API}/accounts/${account.id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchFiles();
      if (onFilesChange) onFilesChange();
      toast.success("Documents uploaded successfully");
    } catch (error) {
      console.error("Failed to upload files:", error);
      toast.error("Failed to upload some files. Please check file size (max 10MB) and type.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDocsChange = async () => {
    await fetchFiles();
    setDocsRefresh((d) => d + 1);
    if (onFilesChange) onFilesChange();
  };

  return (
    <div className="h-full flex flex-col" data-testid="account-detail">
      {/* ─── Compact header: logo · title · inline meta · actions · drop-zone ─── */}
      <div
        className={`relative px-4 py-2 border-b border-gray-200 bg-white flex-shrink-0 transition-colors ${
          dragOver ? "bg-blue-50 ring-2 ring-blue-300 ring-inset" : ""
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid="account-detail-header"
      >
        {dragOver && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-blue-50/70 z-10">
            <span className="text-sm font-medium text-blue-700">Drop files to upload</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="flex-shrink-0">
            {account.logo_filename && logoUrl ? (
              <img
                src={logoUrl}
                alt={`${account.name} logo`}
                className="w-20 h-10 rounded-md object-contain border bg-white"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className={`w-20 h-10 rounded-md bg-gray-100 items-center justify-center border ${
                account.logo_filename && logoUrl ? "hidden" : "flex"
              }`}
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>

          {/* Title + inline meta chips */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900 leading-tight truncate" data-testid="detail-account-name">
                {account.name}
              </h2>
              <span
                data-testid="detail-status"
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                  account.status === "Active"
                    ? "bg-green-100 text-green-800"
                    : account.status === "Inactive"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {account.status}
              </span>
              {account.is_paid && account.account_type !== "Bank Account" && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-green-100 text-green-800"
                  data-testid="detail-paid-badge"
                >
                  Paid
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs text-gray-600 leading-snug">
              <span className="text-gray-500">{account.account_type}</span>
              {account.company_provider && (
                <span data-testid="detail-provider" className="truncate">
                  {account.company_provider}
                </span>
              )}
              {account.account_number && (
                <span title={`Account #${account.account_number}`} className="truncate">
                  #{account.account_number}
                </span>
              )}
              {account.payment_due_date && (
                <span data-testid="detail-payment-due">
                  Due{" "}
                  {new Date(account.payment_due_date + "T00:00:00").toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                  {account.payment_recurrence && account.payment_recurrence !== "One-time" && (
                    <span className="ml-1 text-gray-400">· {account.payment_recurrence}</span>
                  )}
                </span>
              )}
              {account.amount_due != null && account.account_type !== "Bank Account" && (
                <span data-testid="detail-amount-due" className="font-semibold text-gray-900">
                  {formatMoney(account.amount_due, settings)}
                </span>
              )}
              {account.notes && (
                <button
                  type="button"
                  onClick={() => setShowNotes((v) => !v)}
                  data-testid="detail-notes-toggle"
                  title={showNotes ? "Hide notes" : "Show notes"}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {showNotes ? "hide notes" : "notes"}
                </button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.eml,.msg"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              id="file-upload"
              data-testid="file-upload-input"
            />
            <label
              htmlFor="file-upload"
              title="Upload documents (or drop them anywhere on this header) · max 10MB each"
              data-testid="header-upload-button"
              className="cursor-pointer inline-flex items-center gap-1 border border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700 px-2 py-1.5 rounded-md text-xs font-medium"
            >
              {uploading ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Uploading…
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0l-4 4m4-4l4 4" />
                  </svg>
                  Upload
                </>
              )}
            </label>
            {account.url && (
              <a
                href={account.url}
                target="_blank"
                rel="noopener noreferrer"
                title={account.url}
                data-testid="detail-url"
                className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded-md text-xs font-medium"
              >
                URL
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0-7L10 14m-4-7H5a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-1" />
                </svg>
              </a>
            )}
            <button
              onClick={() => setShowEditModal(true)}
              data-testid="edit-account-button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          </div>
        </div>

        {account.notes && showNotes && (
          <div
            className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-700 whitespace-pre-wrap"
            data-testid="detail-notes"
          >
            {account.notes}
          </div>
        )}
      </div>

      {/* ─── Body: statements/requirements/documents ─── */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
      {/* Files List (non-monthly) or Statements/Rent (monthly / tenant accounts) */}
        {account.billing_mode === "invoices" ? (
          <>
            <InvoicesPanel
              account={account}
              refreshKey={docsRefresh}
              onChange={async () => {
                await fetchFiles();
                if (onUpdate) onUpdate();
              }}
            />
            <div className="mt-6 pt-4 border-t border-gray-200">
              <DocumentsSection
                account={account}
                files={files.filter((f) => !f.invoice_id)}
                onFilesChange={handleDocsChange}
                enableInvoiceFiling
              />
            </div>
          </>
        ) : account.account_type === "Tenant" ||
        (account.payment_recurrence === "Monthly" && account.account_type !== "Contract") ? (
          <>
            <StatementsGrid
              account={account}
              onOpenStatement={onOpenStatement}
              title={account.account_type === "Tenant" ? "Rent" : "Statements"}
              refreshKey={(statementsRefreshKey || 0) + docsRefresh}
              extractingStatementIds={extractingStatementIds}
              onExtract={onExtract}
              onViewModeChange={onStatementsViewModeChange}
            />
            <div className="mt-6 pt-4 border-t border-gray-200">
              <DocumentsSection
                account={account}
                files={files.filter((f) => !f.statement_id)}
                onFilesChange={handleDocsChange}
                enableStatementFiling
              />
            </div>
          </>
        ) : (
        <div>
          {account.account_type === "Contract" && (
            <RequirementsList account={account} onChange={onFilesChange} />
          )}
          <DocumentsSection account={account} files={files} onFilesChange={handleDocsChange} />
        </div>
        )}
      </div>

      {/* Edit Account Modal */}
      {showEditModal && (
        <EditAccountModal
          account={account}
          onClose={() => setShowEditModal(false)}
          onUpdate={onUpdate}
          onLogoUpdate={onLogoUpdate}
          currentLogoUrl={logoUrl}
        />
      )}
    </div>
  );
};

export default AccountDetail;
